import { Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, ValidationErrors, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';

import { OrderPaymentRefund, TechnicalInspectionService } from '@app/technical-inspections/_services/technical-inspection.service';
import { displayErrorFromUnknown } from '@app/_utils/error-util';

export interface BankOperationsDialogData {
  techInspId: number;
  existingTransactionNumber?: string;
  existingPaymentDate?: string;
  orderNumber?: string;
  orderTotalAmount?: number;
  orderPaymentStatusId?: number;
  orderPaymentId?: number;
  inspectionStatusName?: string;
  orderStatusId?: number;
}

export interface BankOperationsDialogResult {
  refreshed: boolean;
}

const REFUND_TYPE_FULL    = 1;
const REFUND_TYPE_PARTIAL = 2;
const PAID_TECH_INSP_STATUS_NAME = 'Платен';
const FINISHED_TECH_INSP_STATUS_NAME = 'Завършен';
const ORDER_PAID_ID = 20;
const ORDER_PAYMENT_PAID_ID = 41;

@Component({
  selector: 'app-bank-operations-dialog',
  templateUrl: './bank-operations-dialog.component.html',
  styleUrls: ['./bank-operations-dialog.component.css']
})
export class BankOperationsDialogComponent implements OnInit {
  submitting = false;
  refundFormVisible = false;
  existingRefunds: OrderPaymentRefund[] = [];
  refundsLoaded = false;

  readonly REFUND_TYPE_FULL = REFUND_TYPE_FULL;
  readonly REFUND_TYPE_PARTIAL = REFUND_TYPE_PARTIAL;

  form = this.fb.group({
    transactionId: ['', [Validators.required, Validators.maxLength(30)]]
  });

  refundForm = this.fb.group({
    refundTypeId: [null as number | null, Validators.required],
    amount: [null as number | null, [Validators.required, this.amountValidator.bind(this)]],
    reason: ['', [Validators.required, Validators.maxLength(1000)]]
  });

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<BankOperationsDialogComponent, BankOperationsDialogResult>,
    @Inject(MAT_DIALOG_DATA) public data: BankOperationsDialogData,
    private techInspService: TechnicalInspectionService,
    private translate: TranslateService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    if (this.refundEligible) {
      this.techInspService.getRefunds(this.data.techInspId).subscribe({
        next: (refunds) => {
          this.existingRefunds = refunds || [];
          this.refundsLoaded = true;
        },
        error: () => {
          this.refundsLoaded = true;
        }
      });
    }
  }

  get refundEligible(): boolean {
    const inspStatusOk = this.data.inspectionStatusName === PAID_TECH_INSP_STATUS_NAME
                      || this.data.inspectionStatusName === FINISHED_TECH_INSP_STATUS_NAME;
    return inspStatusOk
        && this.data.orderStatusId === ORDER_PAID_ID
        && this.data.orderPaymentStatusId === ORDER_PAYMENT_PAID_ID;
  }
  
  get hasValidExistingTransaction(): boolean {
    const t = (this.data.existingTransactionNumber || '').trim();
    if (t.length < 10) {
      return false;
    }
    if (/^-+$/.test(t)) {
      return false;
    }
    return true;
  }

  get totalRefunded(): number {
    return (this.existingRefunds || []).reduce((sum, r) => sum + Number(r.totalAmount), 0);
  }

  get remaining(): number {
    const total = Number(this.data.orderTotalAmount || 0);
    return total - this.totalRefunded;
  }

  get canAddRefund(): boolean {
    return this.refundEligible && this.refundsLoaded && this.remaining > 0;
  }

  showAddRefund(): void {
    this.refundFormVisible = true;
    this.refundForm.reset({ refundTypeId: null, amount: null, reason: '' });
  }

  cancelAddRefund(): void {
    this.refundFormVisible = false;
    this.refundForm.reset({ refundTypeId: null, amount: null, reason: '' });
  }

  onRefundTypeChange(): void {
    const typeId = Number(this.refundForm.value.refundTypeId);
    const amountCtrl = this.refundForm.get('amount');
    if (!amountCtrl) return;

    if (typeId === REFUND_TYPE_FULL) {
      amountCtrl.setValue(this.remaining);
      amountCtrl.disable();
    } else {
      amountCtrl.enable();
      amountCtrl.setValue(null);
    }
    amountCtrl.updateValueAndValidity();
  }

  amountValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (value === null || value === undefined || value === '') return null;

    const num = Number(value);
    if (isNaN(num)) return { invalidNumber: true };

    const typeId = Number(this.refundForm?.value?.refundTypeId);
    const remaining = this.remaining;

    if (typeId === REFUND_TYPE_FULL) {
      if (num !== remaining) return { amountMismatch: true };
      return null;
    }

    if (typeId === REFUND_TYPE_PARTIAL) {
      if (num <= 0) return { amountTooLow: true };
      if (num >= remaining) return { amountTooHigh: true };
      return null;
    }

    return null;
  }

  submitRefund(): void {
    if (this.refundForm.invalid || this.submitting) return;

    const refundTypeId = Number(this.refundForm.value.refundTypeId);
    const amountValue = this.refundForm.get('amount')?.value;
    const amount = Number(amountValue);
    const reason = (this.refundForm.value.reason ?? '').toString().trim();

    if (!refundTypeId || !amount || !reason) return;

    this.submitting = true;
    this.techInspService.refundOrderPayment({
      techInspId: this.data.techInspId,
      refundTypeId,
      amount,
      reason
    }).subscribe({
      next: () => {
        this.submitting = false;
        this.toastr.success(this.translate.instant('technicalInspection.bankOps.refund.success'));
        this.dialogRef.close({ refreshed: true });
      },
      error: (err) => {
        this.submitting = false;
        const code: string | undefined = err?.error?.code;
        if (code && code.startsWith('DAP')) {
          this.toastr.error(this.translate.instant('errorCodes.' + code));
        } else {
          displayErrorFromUnknown(err);
          this.toastr.error(this.translate.instant('messages.errorLoadingData'));
        }
      }
    });
  }

  onSubmit(): void {
    if (this.form.invalid || this.submitting) {
      return;
    }
    const transactionId = (this.form.value.transactionId ?? '').toString().trim();
    if (!transactionId) {
      return;
    }

    this.submitting = true;
    this.techInspService.markAsAlreadyPaid(this.data.techInspId, transactionId).subscribe({
      next: () => {
        this.submitting = false;
        this.toastr.success(this.translate.instant('technicalInspection.bankOps.success'));
        this.dialogRef.close({ refreshed: true });
      },
      error: (err) => {
        this.submitting = false;
        const code: string | undefined = err?.error?.code;
        if (code && code.startsWith('DAP')) {
          this.toastr.error(this.translate.instant('errorCodes.' + code));
        } else {
          displayErrorFromUnknown(err);
          this.toastr.error(this.translate.instant('messages.errorLoadingData'));
        }
      }
    });
  }

  onCancel(): void {
    this.dialogRef.close({ refreshed: false });
  }
}
